import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Tags,
    Loader2,
    RefreshCw,
    Send,
    Plus,
    Trash2,
    X,
    AlertTriangle
} from 'lucide-react';

import {
    fetchCategoryList,
    fetchUnitsList,
    fetchDcaList,
    fetchSDcaList,
    fetchHsnList,
    fetchHsnRemarks,
    fetchMajorGroupList,
    fetchSubGroupList,
    fetchSpecificationCodes,
    fetchConnectedSDcaList,
    addItemCategory,
    addMajorGroup,
    addUnit,
    submitItemCode,
    clearDcaCascade,
    clearSDcaList,
    clearHsnRemarks,
    clearSubGroupList,
    clearSpecCodeList,
    clearSubmitResult,
    resetItemCodeCreation,
    selectICCCategoryList,
    selectICCUnitsList,
    selectICCDcaList,
    selectICCSDcaList,
    selectICCHsnList,
    selectICCHsnRemarks,
    selectICCMajorGroupList,
    selectICCSubGroupList,
    selectICCSpecCodeList,
    selectICCConnectedSdcaList,
    selectICCSubmitResult,
    selectICCLoading,
    selectICCErrors,
} from '../../slices/purchaseSlice/itemCodeCreationSlice';

// ── Shared UI primitives (mirrors ItemsTransfer / IndentAmend pages) ────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo', action }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <h3 className="font-semibold text-sm">{title}</h3>
                    {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
                </div>
                {action}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const AddLink = ({ children, onClick }) => (
    <button type="button" onClick={onClick}
        className="text-xs font-semibold underline text-indigo-700 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-100">
        {children}
    </button>
);

const ModalShell = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 dark:text-white">{title}</h3>
                <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                    <X className="w-4 h-4" />
                </button>
            </div>
            {children}
        </div>
    </div>
);

const ITEM_TYPES = [
    { value: 'Client', label: 'Client Item Code' },
    { value: 'Vendor', label: 'Vendor Item Code' },
];

const CATEGORY_CODE_OPTIONS = ['1', '2', '3', '4'];
const CONNECTED_CATEGORY_IDS = ['2', '3', '4'];
const ADD_SUBGROUP_VALUE = '00';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// This page's own amount validator (distinct from the 3-decimal one used on stock pages):
// digits, one decimal point, max 2 fraction digits — legacy's keypress ValidateAmountQuantity/ValidateAmount
// check the value *before* the key lands, so they let the second fraction digit through.
const isValidAmountString = (v) => {
    if (v === '') return true;
    return /^\d*\.?\d{0,2}$/.test(v);
};

// spSaveItemCodeDetails splits trader/link lists on '|', so the character can't appear inside a value
const noPipe = (v) => v.replace(/\|/g, '');

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const categoryName = (cat) => (cat?.CategoryValue?.split(',')[1] || '').trim();

let rowIdSeq = 1;
const nextRowId = () => rowIdSeq++;

const emptySupplierRow = (deletable) => ({ id: nextRowId(), name: '', email: '', phone: '', rate: '', checked: false, deletable });
const emptyOnlineRow = (deletable) => ({ id: nextRowId(), link: '', rate: '', checked: false, deletable });

const ItemCodeCreation = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || '';
    const createdBy = userData?.userName || userData?.employeeId || userData?.UID || 'system';

    const categoryList      = useSelector(selectICCCategoryList);
    const unitsList          = useSelector(selectICCUnitsList);
    const dcaList             = useSelector(selectICCDcaList);
    const sdcaList             = useSelector(selectICCSDcaList);
    const hsnList               = useSelector(selectICCHsnList);
    const hsnRemarks             = useSelector(selectICCHsnRemarks);
    const majorGroupList          = useSelector(selectICCMajorGroupList);
    const subGroupList             = useSelector(selectICCSubGroupList);
    const specCodeList              = useSelector(selectICCSpecCodeList);
    const connectedSdcaList          = useSelector(selectICCConnectedSdcaList);
    const submitResult                = useSelector(selectICCSubmitResult);
    const loading                      = useSelector(selectICCLoading);
    const errors                        = useSelector(selectICCErrors);

    const [itemType, setItemType] = useState('');
    const [category, setCategory] = useState('');
    const [dca, setDca] = useState('');
    const [sdca, setSdca] = useState('');
    const [connectedSdca, setConnectedSdca] = useState('');
    const [hsn, setHsn] = useState('');
    const [majorGroup, setMajorGroup] = useState('');
    const [subGroupSelect, setSubGroupSelect] = useState('');
    const [subGroupCode, setSubGroupCode] = useState('');
    const [specificationCode, setSpecificationCode] = useState('');
    const [specification, setSpecification] = useState('');
    const [units, setUnits] = useState('');
    const [basicPrice, setBasicPrice] = useState('');
    const [itemName, setItemName] = useState('');
    const [remarks, setRemarks] = useState('');

    const [suppliers, setSuppliers] = useState([emptySupplierRow(false)]);
    const [onlineRows, setOnlineRows] = useState([emptyOnlineRow(false)]);

    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [newCategoryCodeId, setNewCategoryCodeId] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');

    const [showMajorGroupModal, setShowMajorGroupModal] = useState(false);
    const [newMajorGroupCode, setNewMajorGroupCode] = useState('');
    const [newMajorGroupName, setNewMajorGroupName] = useState('');

    const [showUnitsModal, setShowUnitsModal] = useState(false);
    const [newUnitName, setNewUnitName] = useState('');

    const [showConfirm, setShowConfirm] = useState(false);
    const [previewItemCode, setPreviewItemCode] = useState('');

    const selectedCategory = useMemo(() => categoryList.find((c) => c.Categoryid === category), [categoryList, category]);
    const selectedCategoryName = selectedCategory ? categoryName(selectedCategory) : '';
    // Categoryid is the raw CategoryCode column; legacy trims it before building the item code
    // (@ItemCodestartswith is varchar(1)) and before the 2/3/4 connected-SDCA check.
    const categoryId = (category || '').trim();
    const isConnectedCategory = CONNECTED_CATEGORY_IDS.includes(categoryId);

    useEffect(() => {
        dispatch(fetchCategoryList());
        dispatch(fetchUnitsList());
        return () => dispatch(resetItemCodeCreation());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (submitResult) {
            toast.success('Item Code Created Successfully');
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // Surfaces cascade-load failures the way legacy's ajax `failure`/`error` handlers alerted them.
    useEffect(() => {
        const msg = errors.dcaList || errors.hsnList || errors.majorGroupList
            || errors.sdcaList || errors.connectedSdcaList;
        if (msg) toast.error(msg);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors.dcaList, errors.hsnList, errors.majorGroupList, errors.sdcaList, errors.connectedSdcaList]);

    // ── Category cascade ─────────────────────────────────────────────────────

    const handleCategoryChange = (val) => {
        setCategory(val);
        setDca(''); setSdca(''); setConnectedSdca(''); setHsn('');
        setMajorGroup(''); setSubGroupSelect(''); setSubGroupCode(''); setSpecificationCode('');
        dispatch(clearDcaCascade());
        if (!val) return;
        dispatch(fetchDcaList({ categoryId: val }));
        dispatch(fetchHsnList({ categoryId: val }));
        const cat = categoryList.find((c) => c.Categoryid === val);
        dispatch(fetchMajorGroupList({ val, txt: cat ? categoryName(cat) : '' }));
        if (CONNECTED_CATEGORY_IDS.includes(val.trim())) dispatch(fetchConnectedSDcaList());
    };

    const handleDcaChange = (val) => {
        setDca(val);
        setSdca('');
        dispatch(clearSDcaList());
        if (val) dispatch(fetchSDcaList({ dcaCode: val }));
    };

    const handleHsnChange = (val) => {
        setHsn(val);
        if (val) dispatch(fetchHsnRemarks({ hsnId: val }));
        else dispatch(clearHsnRemarks());
    };

    const handleMajorGroupChange = (val) => {
        setMajorGroup(val);
        setSubGroupSelect(''); setSubGroupCode(''); setSpecificationCode('');
        dispatch(clearSubGroupList());
        if (val) dispatch(fetchSubGroupList({ cat: categoryId, grp: val }));
    };

    const handleSubGroupSelectChange = (val) => {
        setSubGroupSelect(val);
        if (!val) {
            setSubGroupCode(''); setSpecificationCode('');
            dispatch(clearSpecCodeList());
        } else if (val === ADD_SUBGROUP_VALUE) {
            setSubGroupCode('');
            setSpecificationCode('01');
            dispatch(clearSpecCodeList());
        } else {
            setSubGroupCode(val);
            dispatch(fetchSpecificationCodes({ cat: categoryId, mGrp: majorGroup, sGrp: val }));
        }
    };

    // Mirrors legacy's each-loop-overwrite: the last returned row determines the next code.
    useEffect(() => {
        if (!subGroupSelect || subGroupSelect === ADD_SUBGROUP_VALUE) return;
        if (specCodeList.length === 0) { setSpecificationCode('01'); return; }
        const last = specCodeList[specCodeList.length - 1];
        const code = parseInt(last?.Specificationcode, 10);
        if (isNaN(code)) { setSpecificationCode('01'); return; }
        if (code < 99) {
            const nextCode = code + 1;
            setSpecificationCode(nextCode < 10 ? `0${nextCode}` : `${nextCode}`);
        } else {
            setSpecificationCode('');
            toast.error('SpecificationCode Exceeded 99 items');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specCodeList]);

    // ── Add Category modal ───────────────────────────────────────────────────

    const handleAddCategory = () => {
        if (!newCategoryCodeId) { toast.error('Select Item Category Code'); return; }
        if (!newCategoryName.trim()) { toast.error('Enter Item Category Name'); return; }
        dispatch(addItemCategory({ ItemCategoryCode: newCategoryCodeId, ItemCategoryName: newCategoryName.trim() })).then((result) => {
            if (addItemCategory.fulfilled.match(result)) {
                toast.success('Category Added Successfully.');
                setShowCategoryModal(false);
                setNewCategoryCodeId(''); setNewCategoryName('');
            } else {
                toast.error(result.payload || 'Failed to add category');
            }
        });
    };

    // ── Add Major Group modal ────────────────────────────────────────────────

    const handleAddMajorGroup = () => {
        if (!category) { toast.error('Select Category Code/Name'); return; }
        if (!newMajorGroupCode.trim()) { toast.error('Enter Major Group Code'); return; }
        if (newMajorGroupCode.trim().length !== 2) { toast.error('Invalid Major Group Code'); return; }
        if (!newMajorGroupName.trim()) { toast.error('Enter Major Group Name'); return; }
        dispatch(addMajorGroup({
            ItemCategoryCode: category,
            ItemCategoryName: selectedCategoryName,
            Majorgroupcode: newMajorGroupCode.trim().toUpperCase(),
            Majorgroupname: newMajorGroupName.trim(),
        })).then((result) => {
            if (addMajorGroup.fulfilled.match(result)) {
                toast.success('Major Group Added Successfully.');
                setShowMajorGroupModal(false);
                setNewMajorGroupCode(''); setNewMajorGroupName('');
            } else {
                toast.error(result.payload || 'Failed to add major group');
            }
        });
    };

    // ── Add Units modal ──────────────────────────────────────────────────────

    const handleAddUnit = () => {
        if (!newUnitName.trim()) { toast.error('Enter Units'); return; }
        dispatch(addUnit({ Units: newUnitName.trim() })).then((result) => {
            if (addUnit.fulfilled.match(result)) {
                toast.success('Units Added Successfully.');
                setShowUnitsModal(false);
                setNewUnitName('');
            } else {
                toast.error(result.payload || 'Failed to add unit');
            }
        });
    };

    // ── Local Traders grid ───────────────────────────────────────────────────

    const updateSupplier = (id, field, value) => {
        setSuppliers((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    };

    const handleSupplierPhoneChange = (id, value) => {
        const digits = value.replace(/\D/g, '').slice(0, 10);
        updateSupplier(id, 'phone', digits);
    };

    const handleSupplierRateChange = (id, value) => {
        if (!isValidAmountString(value)) return;
        updateSupplier(id, 'rate', value);
    };

    const handleAddSupplierRow = () => {
        const names = suppliers.map((r) => r.name.trim()).filter(Boolean);
        const dupes = names.filter((n, i) => names.indexOf(n) !== i);
        if (dupes.length > 0) { toast.error('Duplicate Supplier Found'); return; }
        if (suppliers.some((r) => !r.name.trim())) { toast.error('Enter Supplier Name'); return; }
        if (suppliers.some((r) => !r.email.trim())) { toast.error('Enter Supplier MailId'); return; }
        if (suppliers.some((r) => !r.phone.trim())) { toast.error('Enter Supplier Phone No'); return; }
        if (suppliers.some((r) => !r.rate || num(r.rate) <= 0)) { toast.error('Enter Supplier Rate'); return; }
        setSuppliers((prev) => [...prev, emptySupplierRow(true)]);
    };

    const handleDeleteSupplierRow = (id) => {
        setSuppliers((prev) => prev.filter((r) => r.id !== id));
    };

    // ── Online Prices grid ───────────────────────────────────────────────────

    const updateOnline = (id, field, value) => {
        setOnlineRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    };

    const handleOnlineRateChange = (id, value) => {
        if (!isValidAmountString(value)) return;
        updateOnline(id, 'rate', value);
    };

    const handleAddOnlineRow = () => {
        for (const row of onlineRows) {
            if (row.link.trim() && !row.rate.trim()) { toast.error('Please Enter Online Rate'); return; }
            if (!row.link.trim() && row.rate.trim()) { toast.error('Please Enter Link'); return; }
        }
        const links = onlineRows.map((r) => r.link.trim()).filter(Boolean);
        const dupes = links.filter((l, i) => links.indexOf(l) !== i);
        if (dupes.length > 0) { toast.error('Duplicate Link'); return; }
        setOnlineRows((prev) => [...prev, emptyOnlineRow(true)]);
    };

    const handleDeleteOnlineRow = (id) => {
        setOnlineRows((prev) => prev.filter((r) => r.id !== id));
    };

    // ── Submit ───────────────────────────────────────────────────────────────

    const validateBeforeConfirm = () => {
        if (!itemType) { toast.error('Select Item Type'); return false; }
        if (!category) { toast.error('Select Category Code/Name'); return false; }
        if (isConnectedCategory && !connectedSdca) { toast.error('Select Connected SDCA'); return false; }
        if (!dca) { toast.error('Select DCA'); return false; }
        if (!sdca) { toast.error('Select SDCA'); return false; }
        if (!hsn) { toast.error('Select HSN'); return false; }
        if (!majorGroup) { toast.error('Select Majour Group Code'); return false; }
        if (!subGroupSelect) { toast.error('Select Sub Group Code'); return false; }
        if (!subGroupCode || ['0', '00', '000'].includes(subGroupCode)) { toast.error('Please Enter Sub Group Code'); return false; }
        if (subGroupCode.length !== 3) { toast.error('Invalid Sub Group Code'); return false; }
        // A "new" sub group that already exists would get spec code 01 and collide with the
        // existing item (SP answers "Item Code Already Exists") — point the user to the list instead.
        if (subGroupSelect === ADD_SUBGROUP_VALUE && subGroupList.some((g) => String(g.Subgroupval).trim() === subGroupCode)) {
            toast.error(`Sub Group Code ${subGroupCode} already exists — select it from the Sub Group Code list`);
            return false;
        }
        if (!specificationCode) { toast.error('Invalid Specification Code'); return false; }
        if (!specification.trim()) { toast.error('Please Enter Specification'); return false; }
        if (!units) { toast.error('Select Units'); return false; }
        if (basicPrice === '' || (num(basicPrice) <= 0) || basicPrice === '.') { toast.error('Invalid Basic Price'); return false; }
        if (!itemName.trim()) { toast.error('Please Enter Item Name'); return false; }
        if (suppliers.length < 2) { toast.error('Minimun TWO Traders Required'); return false; }
        if (!suppliers.every((r) => r.checked)) { toast.error('Please Verify Trader'); return false; }
        if (suppliers.some((r) => !r.name.trim())) { toast.error('Enter Trader Name'); return false; }
        if (suppliers.some((r) => !r.email.trim())) { toast.error('Enter Trader Mail-Id'); return false; }
        const badEmail = suppliers.find((r) => !isValidEmail(r.email.trim()));
        if (badEmail) { toast.error(`Invalid Mail-Id for ${badEmail.name}`); return false; }
        if (suppliers.some((r) => !r.phone.trim())) { toast.error('Enter Trader PhoneNo'); return false; }
        const badPhone = suppliers.find((r) => r.phone.length !== 10);
        if (badPhone) { toast.error(`Invalid Phone No for ${badPhone.name}`); return false; }
        if (suppliers.some((r) => !r.rate || num(r.rate) <= 0)) { toast.error('Enter Local Trader Rate'); return false; }
        const supNames = suppliers.map((r) => r.name.trim());
        if (supNames.some((n, i) => supNames.indexOf(n) !== i)) { toast.error('Duplicate Supplier'); return false; }

        for (const row of onlineRows) {
            if (row.checked === false && row.link.trim() && row.rate.trim()) { toast.error('Please VerifyLink'); return false; }
            if (!row.link.trim() && row.rate.trim()) { toast.error('Please Enter Link'); return false; }
            if (row.link.trim() && !row.rate.trim()) { toast.error('Please Enter Online Rate'); return false; }
        }
        const onlineLinks = onlineRows.map((r) => r.link.trim()).filter(Boolean);
        if (onlineLinks.some((l, i) => onlineLinks.indexOf(l) !== i)) { toast.error('Duplicate Supplier'); return false; }

        if (!remarks.trim()) { toast.error('Enter Remarks'); return false; }
        return true;
    };

    const handleSubmitClick = () => {
        if (!validateBeforeConfirm()) return;
        setPreviewItemCode(`${categoryId}${majorGroup}${subGroupCode}${specificationCode}`);
        setShowConfirm(true);
    };

    const handleConfirmSave = () => {
        const hsnCode = hsnList.find((h) => String(h.HSNID) === String(hsn))?.HSNCode || '';
        const validOnline = onlineRows.filter((r) => r.link.trim() && r.rate.trim());

        dispatch(submitItemCode({
            ItemCode: previewItemCode,
            ItemCodestartswith: categoryId,
            ItemCodecatname: selectedCategoryName,
            Itemdca: dca,
            Itemsdca: sdca,
            HSNCode: hsnCode,
            Majorgroupcode: majorGroup,
            Subgrouptxtcode: subGroupCode,
            Specificationcode: specificationCode,
            Specification: specification,
            Units: units,
            Basicprice: basicPrice,
            ItemCodeType: itemType,
            Itemname: itemName,
            RoleID: String(roleId),
            Createdby: createdBy,
            SupName: `${suppliers.map((r) => r.name.trim()).join('|')}|`,
            Supemail: `${suppliers.map((r) => r.email.trim()).join('|')}|`,
            Supphone: `${suppliers.map((r) => r.phone.trim()).join('|')}|`,
            SupRate: `${suppliers.map((r) => r.rate).join('|')}|`,
            Link: validOnline.length ? `${validOnline.map((r) => r.link.trim()).join('|')}|` : '',
            LinkRate: validOnline.length ? `${validOnline.map((r) => r.rate).join('|')}|` : '',
            Remarks: remarks,
            ConnectedSDca: connectedSdca,
        }));
        setShowConfirm(false);
    };

    const handleReset = () => {
        setItemType(''); setCategory(''); setDca(''); setSdca(''); setConnectedSdca('');
        setHsn(''); setMajorGroup(''); setSubGroupSelect(''); setSubGroupCode(''); setSpecificationCode('');
        setSpecification(''); setUnits(''); setBasicPrice(''); setItemName(''); setRemarks('');
        setSuppliers([emptySupplierRow(false)]);
        setOnlineRows([emptyOnlineRow(false)]);
        dispatch(clearDcaCascade());
        dispatch(fetchCategoryList());
        dispatch(fetchUnitsList());
    };

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <Tags className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Item Code Creation</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Create a new master item code with category, group and pricing details.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Item Category ────────────────────────────────────────────── */}
            <SectionCard icon={Tags} title="Item Category" accent="indigo">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Label required>Item Code Type</Label>
                        <select className={inputCls} value={itemType} onChange={(e) => setItemType(e.target.value)}>
                            <option value="">-Please Select-</option>
                            {ITEM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <div className="flex items-center justify-between">
                            <Label required>Category Code/Name</Label>
                            <AddLink onClick={() => setShowCategoryModal(true)}>Add Item Category</AddLink>
                        </div>
                        <select className={inputCls} value={category} onChange={(e) => handleCategoryChange(e.target.value)} disabled={loading.categoryList}>
                            <option value="">-Please Select-</option>
                            {categoryList.map((c) => (
                                <option key={c.Categoryid} value={c.Categoryid}>{`${c.Categoryid.trim()} - ${categoryName(c)}`}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label required>DCA</Label>
                        <select className={inputCls} value={dca} onChange={(e) => handleDcaChange(e.target.value)} disabled={!category || loading.dcaList}>
                            <option value="">-Please Select-</option>
                            {dcaList.map((d) => <option key={d.DCACode} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label required>SDCA</Label>
                        <select className={inputCls} value={sdca} onChange={(e) => setSdca(e.target.value)} disabled={!dca || loading.sdcaList}>
                            <option value="">-Please Select-</option>
                            {sdcaList.map((d) => <option key={d.SubDCACode} value={d.SubDCACode}>{d.SubDCAIDSTR}</option>)}
                        </select>
                    </div>
                    {isConnectedCategory && (
                        <div>
                            <Label required>Connected SDCA</Label>
                            <select className={inputCls} value={connectedSdca} onChange={(e) => setConnectedSdca(e.target.value)} disabled={loading.connectedSdcaList}>
                                <option value="">-Please Select-</option>
                                {connectedSdcaList.map((d) => <option key={d.ConSubDCACode} value={d.ConSubDCACode}>{d.ConSubDCAIDSTR}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <Label required>HSN Code</Label>
                        <select className={inputCls} value={hsn} onChange={(e) => handleHsnChange(e.target.value)} disabled={!category || loading.hsnList}>
                            <option value="">-Please Select-</option>
                            {hsnList.map((h) => <option key={h.HSNID} value={h.HSNID}>{h.HSNCode}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-3">
                        <Label>HSN Remarks</Label>
                        <input type="text" readOnly className={inputCls} value={loading.hsnRemarks ? 'Loading…' : hsnRemarks} />
                    </div>
                </div>
            </SectionCard>

            {/* ── Group Code ───────────────────────────────────────────────── */}
            <SectionCard icon={Tags} title="Group Code" accent="amber">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <div className="flex items-center justify-between">
                            <Label required>Major Group Code</Label>
                            <AddLink onClick={() => setShowMajorGroupModal(true)}>Add Major Group Code</AddLink>
                        </div>
                        <select className={inputCls} value={majorGroup} onChange={(e) => handleMajorGroupChange(e.target.value)} disabled={!category || loading.majorGroupList}>
                            <option value="">-Please Select-</option>
                            {majorGroupList.map((g) => <option key={g.Majorgroupval} value={g.Majorgroupval}>{g.Majorgrouptext}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label required>Sub Group Code</Label>
                        <select className={inputCls} value={subGroupSelect} onChange={(e) => handleSubGroupSelectChange(e.target.value)} disabled={!majorGroup || loading.subGroupList}>
                            <option value="">-Please Select-</option>
                            <option value={ADD_SUBGROUP_VALUE}>Add NewSubGroup Code</option>
                            {subGroupList.map((g) => <option key={g.Subgroupval} value={g.Subgroupval}>{g.Subgrouptext}</option>)}
                        </select>
                    </div>
                    {subGroupSelect && (
                        <div>
                            <Label required>Sub Group Code (3-digit)</Label>
                            <input type="text" maxLength={3}
                                readOnly={subGroupSelect !== ADD_SUBGROUP_VALUE}
                                className={inputCls}
                                value={subGroupCode}
                                onChange={(e) => setSubGroupCode(e.target.value.replace(/[^0-9]/g, ''))} />
                        </div>
                    )}
                </div>
            </SectionCard>

            {/* ── Specification ────────────────────────────────────────────── */}
            <SectionCard icon={Tags} title="Specification" accent="purple">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div>
                        <Label>Specification Code</Label>
                        <input type="text" readOnly className={inputCls} value={specificationCode} />
                    </div>
                    <div className="md:col-span-5">
                        <Label required>Specification</Label>
                        <input type="text" className={inputCls} value={specification} onChange={(e) => setSpecification(e.target.value)} />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <div className="flex items-center justify-between">
                            <Label required>Units</Label>
                            <AddLink onClick={() => setShowUnitsModal(true)}>Add Units</AddLink>
                        </div>
                        <select className={inputCls} value={units} onChange={(e) => setUnits(e.target.value)} disabled={loading.unitsList}>
                            <option value="">-Please Select-</option>
                            {unitsList.map((u) => <option key={u.Unitsval} value={u.Unitsval}>{u.Unitstext}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <Label required>Item Name</Label>
                        <input type="text" className={`${inputCls} uppercase`} value={itemName} onChange={(e) => setItemName(e.target.value.toUpperCase())} />
                    </div>
                    <div>
                        <Label required>Basic Price</Label>
                        <input type="text" inputMode="decimal" className={inputCls} value={basicPrice}
                            onChange={(e) => { if (isValidAmountString(e.target.value)) setBasicPrice(e.target.value); }} />
                    </div>
                </div>
            </SectionCard>

            {/* ── Local Traders ─────────────────────────────────────────────── */}
            <SectionCard icon={Tags} title="Local Traders" subtitle="Minimum two traders required" accent="indigo">
                <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                {['Chk', 'Trader Name', 'E-Mail', 'Contact No', 'Rate', ''].map((h) => (
                                    <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {suppliers.map((row, idx) => (
                                <tr key={row.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                    <td className="px-2.5 py-2">
                                        <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                            checked={row.checked} onChange={(e) => updateSupplier(row.id, 'checked', e.target.checked)} />
                                    </td>
                                    <td className="px-1.5 py-1.5 w-48">
                                        <input type="text" maxLength={500} className={inputCls} value={row.name} onChange={(e) => updateSupplier(row.id, 'name', noPipe(e.target.value))} />
                                    </td>
                                    <td className="px-1.5 py-1.5 w-56">
                                        <input type="text" maxLength={50} className={inputCls} value={row.email} onChange={(e) => updateSupplier(row.id, 'email', noPipe(e.target.value))} />
                                    </td>
                                    <td className="px-1.5 py-1.5 w-36">
                                        <input type="text" inputMode="numeric" maxLength={10} className={inputCls} value={row.phone} onChange={(e) => handleSupplierPhoneChange(row.id, e.target.value)} />
                                    </td>
                                    <td className="px-1.5 py-1.5 w-28">
                                        <input type="text" inputMode="decimal" className={inputCls} value={row.rate} onChange={(e) => handleSupplierRateChange(row.id, e.target.value)} />
                                    </td>
                                    <td className="px-1.5 py-1.5">
                                        {row.deletable && (
                                            <button onClick={() => handleDeleteSupplierRow(row.id)}
                                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button onClick={handleAddSupplierRow}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
                    <Plus className="w-4 h-4" /> Add New Supplier
                </button>
            </SectionCard>

            {/* ── Online Prices ─────────────────────────────────────────────── */}
            <SectionCard icon={Tags} title="Online Prices" subtitle="Optional" accent="amber">
                <div className="overflow-x-auto rounded-lg border border-amber-200 dark:border-amber-700">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
                                {['Chk', 'Link', 'Rate', ''].map((h) => (
                                    <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {onlineRows.map((row, idx) => (
                                <tr key={row.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-amber-50/40 dark:bg-amber-900/10'}>
                                    <td className="px-2.5 py-2">
                                        <input type="checkbox" className="w-4 h-4 accent-amber-600 cursor-pointer"
                                            checked={row.checked} onChange={(e) => updateOnline(row.id, 'checked', e.target.checked)} />
                                    </td>
                                    <td className="px-1.5 py-1.5">
                                        <input type="text" maxLength={500} className={inputCls} value={row.link} onChange={(e) => updateOnline(row.id, 'link', noPipe(e.target.value))} />
                                    </td>
                                    <td className="px-1.5 py-1.5 w-28">
                                        <input type="text" inputMode="decimal" className={inputCls} value={row.rate} onChange={(e) => handleOnlineRateChange(row.id, e.target.value)} />
                                    </td>
                                    <td className="px-1.5 py-1.5">
                                        {row.deletable && (
                                            <button onClick={() => handleDeleteOnlineRow(row.id)}
                                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button onClick={handleAddOnlineRow}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold">
                    <Plus className="w-4 h-4" /> Add New Online Price
                </button>
            </SectionCard>

            {/* ── Remarks + Submit ─────────────────────────────────────────── */}
            <SectionCard icon={Tags} title="Remarks" accent="indigo">
                <div>
                    <Label required>Remarks</Label>
                    <textarea rows={3} className={inputCls} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                </div>
                <div className="flex justify-end gap-2">
                    <button onClick={handleReset}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                        Reset
                    </button>
                    <button onClick={handleSubmitClick} disabled={loading.submit}
                        className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                        {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                        {loading.submit ? 'Submitting…' : 'Submit'}
                    </button>
                </div>
            </SectionCard>

            {/* ── Add Category modal ───────────────────────────────────────── */}
            {showCategoryModal && (
                <ModalShell title="Add New Category" onClose={() => setShowCategoryModal(false)}>
                    <div className="space-y-3">
                        <div>
                            <Label required>Item Category Code</Label>
                            <select className={inputCls} value={newCategoryCodeId} onChange={(e) => setNewCategoryCodeId(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {CATEGORY_CODE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label required>Item Category Name</Label>
                            <input type="text" className={`${inputCls} uppercase`} value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value.toUpperCase())} />
                        </div>
                        <div className="flex justify-center gap-3 pt-2">
                            <button onClick={handleAddCategory} disabled={loading.addCategory}
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.addCategory ? 'Submitting…' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </ModalShell>
            )}

            {/* ── Add Major Group modal ────────────────────────────────────── */}
            {showMajorGroupModal && (
                <ModalShell title="Add New MajorGroup" onClose={() => setShowMajorGroupModal(false)}>
                    <div className="space-y-3">
                        <div>
                            <Label required>Major Group Code (2 letters)</Label>
                            <input type="text" maxLength={2} className={`${inputCls} uppercase`}
                                value={newMajorGroupCode}
                                onChange={(e) => setNewMajorGroupCode(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())} />
                        </div>
                        <div>
                            <Label required>Major Group Name</Label>
                            <input type="text" className={`${inputCls} uppercase`} value={newMajorGroupName} onChange={(e) => setNewMajorGroupName(e.target.value.toUpperCase())} />
                        </div>
                        <div className="flex justify-center gap-3 pt-2">
                            <button onClick={handleAddMajorGroup} disabled={loading.addMajorGroup}
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.addMajorGroup ? 'Submitting…' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </ModalShell>
            )}

            {/* ── Add Units modal ──────────────────────────────────────────── */}
            {showUnitsModal && (
                <ModalShell title="Add Units" onClose={() => setShowUnitsModal(false)}>
                    <div className="space-y-3">
                        <div>
                            <Label required>Units</Label>
                            <input type="text" className={inputCls} value={newUnitName} onChange={(e) => setNewUnitName(e.target.value)} />
                        </div>
                        <div className="flex justify-center gap-3 pt-2">
                            <button onClick={handleAddUnit} disabled={loading.addUnit}
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.addUnit ? 'Submitting…' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </ModalShell>
            )}

            {/* ── Confirm item code modal ──────────────────────────────────── */}
            {showConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                            <h3 className="font-bold text-gray-900 dark:text-white">Confirm Item Code</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            Do you want to continue with item code: <span className="font-semibold">{previewItemCode}</span>?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setShowConfirm(false)}
                                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                No
                            </button>
                            <button onClick={handleConfirmSave} disabled={loading.submit}
                                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.submit ? 'Submitting…' : 'Yes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ItemCodeCreation;
